import React, { useCallback, useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useProducts } from '../hooks/useProducts';
import { useUrlState, useUrlObjectState } from '../hooks/useUrlState';
import {
    saveFiltersToStorage,
    loadFiltersFromStorage,
    clearFiltersFromStorage,
    hasUrlParams,
} from '../utils/filterStorage';
import SearchBox from './SearchBox';
import CategoryNav from './CategoryNav';
import PropertyFilters from './PropertyFilters';
import ProductGrid from './ProductGrid';
import Pagination from './Pagination';
import LoadingSpinner from './LoadingSpinner';
import ActiveFilters from './ActiveFilters';
import styles from './ProductApp.module.scss';

// Create React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

/**
 * Main Product Filter Application
 * Manages filter state and coordinates all child components
 */
function ProductFilterContent() {
    // Filter state synced with URL parameters for persistence
    const [search, setSearch] = useUrlState('q', '');
    const [categories, setCategories] = useUrlState('category', [], {
        serialize: (val) => val,
        deserialize: (val) => {
            if (!val) return [];
            return Array.isArray(val) ? val : [val];
        },
    });
    const [propertyFilters, setPropertyFilters] = useUrlObjectState({});
    const [page, setPage] = useUrlState('page', 1, {
        serialize: (val) => String(val),
        deserialize: (val) => parseInt(val, 10) || 1,
    });

    // Track if we've already restored from storage (to prevent loops)
    const hasRestored = useRef(false);

    // Restore filters from sessionStorage on mount (if no URL params present)
    useEffect(() => {
        // Only restore once on initial mount
        if (hasRestored.current) return;
        hasRestored.current = true;

        // If URL already has parameters, don't restore from storage
        // (URL params take priority - user might have shared a link)
        if (hasUrlParams()) return;

        // Try to restore saved filters from sessionStorage
        const savedFilters = loadFiltersFromStorage();
        if (savedFilters) {
            if (savedFilters.search) setSearch(savedFilters.search);
            if (savedFilters.categories?.length > 0) setCategories(savedFilters.categories);
            if (savedFilters.filters && Object.keys(savedFilters.filters).length > 0) {
                setPropertyFilters(savedFilters.filters);
            }
            if (savedFilters.page && savedFilters.page > 1) setPage(savedFilters.page);
        }
    }, [setSearch, setCategories, setPropertyFilters, setPage]);

    // Save filters to sessionStorage whenever they change
    useEffect(() => {
        // Skip on initial mount
        if (!hasRestored.current) return;

        saveFiltersToStorage({
            search,
            categories,
            filters: propertyFilters,
            page,
        });
    }, [search, categories, propertyFilters, page]);

    // Fetch products with React Query
    const { data, isLoading, isFetching, error } = useProducts({
        search,
        categories,
        filters: propertyFilters,
        page,
    });

    // Handle search change
    const handleSearchChange = useCallback((value) => {
        setSearch(value);
        setPage(1); // Reset to first page on search
    }, [setSearch, setPage]);

    // Handle category change
    const handleCategoryChange = useCallback((newCategories) => {
        setCategories(newCategories);
        setPage(1); // Reset to first page on category change
    }, [setCategories, setPage]);

    // Handle property filters change
    const handlePropertyFiltersChange = useCallback((newFilters) => {
        setPropertyFilters(newFilters);
        setPage(1); // Reset to first page on filter
    }, [setPropertyFilters, setPage]);

    // Handle reset all filters
    const handleResetFilters = useCallback(() => {
        setSearch('');
        setCategories([]);
        setPropertyFilters({});
        setPage(1);
        clearFiltersFromStorage(); // Clear sessionStorage as well
    }, [setSearch, setCategories, setPropertyFilters, setPage]);

    // Handle page change
    const handlePageChange = useCallback((newPage) => {
        setPage(newPage);
        // Scroll to top of results
        const resultsElement = document.getElementById('product-results');
        if (resultsElement) {
            resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [setPage]);

    // Handle removing search filter
    const handleRemoveSearch = useCallback(() => {
        setSearch('');
        setPage(1);
    }, [setSearch, setPage]);

    // Handle removing a category filter
    const handleRemoveCategory = useCallback((categorySlug) => {
        setCategories(categories.filter(c => c !== categorySlug));
        setPage(1);
    }, [categories, setCategories, setPage]);

    // Handle removing a property filter
    const handleRemovePropertyFilter = useCallback((handle, value) => {
        const currentValues = propertyFilters[handle] || [];
        const newValues = currentValues.filter(v => v !== value);

        if (newValues.length === 0) {
            // Remove the entire filter group if no values left
            const { [handle]: removed, ...rest } = propertyFilters;
            setPropertyFilters(rest);
        } else {
            // Update with remaining values
            setPropertyFilters({
                ...propertyFilters,
                [handle]: newValues,
            });
        }
        setPage(1);
    }, [propertyFilters, setPropertyFilters, setPage]);

    return (
        <div className={styles.resultContainer}>
            <div className={`${styles.sidebar} ${isFetching ? styles.sidebarLoading : ''}`}>
                <SearchBox
                    value={search}
                    onChange={handleSearchChange}
                />

                <CategoryNav
                    selectedCategories={categories}
                    availableCategories={data?.availableCategories}
                    onChange={handleCategoryChange}
                />

                <PropertyFilters
                    filters={propertyFilters}
                    availableFilters={data?.availableFilters}
                    onChange={handlePropertyFiltersChange}
                    onReset={handleResetFilters}
                />
            </div>

            <div className={styles.results} id="product-results">
                {error && (
                    <div className={styles.noResults}>
                        <p>
                            Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                        >
                            Seite neu laden
                        </button>
                    </div>
                )}

                {!error && (
                    <>
                        <div className={styles.resultsMeta}>
                            <div className={styles.resultCount}>
                                {isLoading ? (
                                    <span className={styles.loadingText}>Produkte werden geladen...</span>
                                ) : data ? (
                                    <>
                                        {data.meta.total > 0 ? (
                                            <>
                                                <strong>{data.meta.total}</strong>{' '}
                                                {data.meta.total === 1 ? 'Produkt gefunden' : 'Produkte gefunden'}
                                            </>
                                        ) : (
                                            'Keine Produkte gefunden'
                                        )}
                                    </>
                                ) : null}
                            </div>
                            {!isLoading && isFetching && (
                                <div className={styles.filterLoading}>
                                    <span className={styles.loadingSpinnerSmall}></span>
                                    Laden...
                                </div>
                            )}
                        </div>

                        {!isLoading && data && (
                            <ActiveFilters
                                search={search}
                                categories={categories}
                                propertyFilters={propertyFilters}
                                onRemoveSearch={handleRemoveSearch}
                                onRemoveCategory={handleRemoveCategory}
                                onRemovePropertyFilter={handleRemovePropertyFilter}
                                onClearAll={handleResetFilters}
                            />
                        )}

                        <div className={styles.productGridWrapper}>
                            <ProductGrid
                                products={data?.data}
                                isLoading={isLoading}
                            />
                        </div>

                        {/* Only show pagination when no filters are active */}
                        {!isLoading && data && !search && categories.length === 0 && !Object.values(propertyFilters).some(arr => arr.length > 0) && (
                            <Pagination
                                currentPage={data.meta.current_page}
                                totalPages={data.meta.last_page}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Root component with React Query Provider
 */
export default function ProductApp() {
    return (
        <QueryClientProvider client={queryClient}>
            <ProductFilterContent />
        </QueryClientProvider>
    );
}
